import './SidebarMenu.scss';
import {
    cloneElement,
    createContext,
    CSSProperties,
    Fragment,
    memo,
    MouseEvent,
    ReactElement,
    ReactNode,
    Ref,
    useCallback,
    useContext,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import clsx from 'clsx';
import { Collapse, UnstyledButton } from '@mantine/core';
import { useLocalStorage, useReducedMotion } from '@mantine/hooks';
import { MdChevronRight, MdClose, MdOutlineDescription, MdOutlineFolder, MdPushPin, MdSearch } from 'react-icons/md';
import Tooltip from '../../atoms/Tooltip';
import { useUi } from '../../provider/UiContext';
import { useAppShell } from './AppShellContext';
import { sidebarStorageKeys } from './sidebarStorage';
import {
    canPinMenuItem,
    findMenuPath,
    flattenMenuItems,
    IAppShellMenuEntry,
    IAppShellMenuItem,
    isMenuBranchUnloaded,
    isMenuGroup,
    searchMenuItems,
} from './sidebarMenuModel';
import { useInitialReveal } from '../../hooks/useInitialReveal';
import { useLatest } from '../../hooks/useLatest';

export interface ISidebarMenuProps {
    /** Пункты меню */
    items: IAppShellMenuItem[];
    /**
     * Справа от подписи пункта, по `id` пункта (счётчик `FolderCountBadge`); в рейле скрыт, кроме счётчика с классом
     * `rf-folder-count-badge--attention` — тот в рейле точкой на иконке. Отдельно от `items`: новые бейджи
     * перерисовывают только бейджи, не пункты.
     */
    badges?: ReadonlyMap<string, ReactNode>;
    /**
     * Сводный счётчик ветки по её `id` — виден, пока ветка свёрнута (и в рейле, по правилу `badges`), чтобы счётчик
     * внутри свёрнутой группы не терялся
     */
    collapsedBadges?: ReadonlyMap<string, ReactNode>;
    /** Ключ активного пункта: его предки раскрываются, панель при раскрытии прокручивается к нему */
    activeId?: string;
    /** Догрузить детей ветки (`hasChildren` без `children`): приложение подставляет их в `items` */
    loadChildren?: (item: IAppShellMenuItem) => Promise<unknown>;
    /** Над поиском (кнопка «Создать документ») */
    top?: ReactNode;
    /** Между поиском и пунктами */
    beforeItems?: ReactNode;
    /** Под пунктами, внизу меню (обучающие туры) */
    bottom?: ReactNode;
    /** Чьи закреплённые пункты (пользователь): ключ `<storageKey>.pins.<pinsScope>` */
    pinsScope?: string | number;
    /** Имя блока закреплённых пунктов */
    pinsLabel?: string;
    /** Поиск ещё догружает ветки */
    searchLoading?: boolean;
    /** Часть веток не загрузилась: в выдаче поиска — кнопка повтора `onSearchRetry` */
    searchFailed?: boolean;
    onSearchRetry?: () => void;
    /** Полное меню видно; без значения — из оболочки (`useAppShell().sidebarOpen`) */
    opened?: boolean;
    /** Показать полное меню (поиск по «/», щелчок по иконке рейла); без значения — из оболочки */
    onRequestOpen?: () => void;
    /** Переход из меню; без значения — из оболочки (закрывает временную панель и Drawer) */
    onNavigate?: () => void;
    /** Класс корня */
    className?: string;
}

const STICKY_TOP_PER_LEVEL = 34;
const CONTROL_SELECTOR = '.rf-folders-link-control';
const LABEL_TEXT_SELECTOR = '.rf-folders-link-control__label-text';
const STUCK_CLASS = 'rf-folders-link-control--stuck';
/** Клавиша «/» в этих местах — ввод, а не поиск по меню */
const TYPING_TARGET =
    'input, textarea, select, [contenteditable]:not([contenteditable="false"]), [role="dialog"], .ace_editor';
const NO_PINS: string[] = [];

const hasModifier = (event: MouseEvent) =>
    event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0;

// Sticky ancestors cover the top of the list: keep the node below them, centred when out of view.
function revealMenuNode(container: HTMLElement, group: HTMLElement) {
    const target = group.querySelector<HTMLElement>(CONTROL_SELECTOR) ?? group;
    let covered = 0;
    for (
        let ancestor = group.parentElement?.closest<HTMLElement>('[data-node-key]');
        ancestor && container.contains(ancestor);
        ancestor = ancestor.parentElement?.closest<HTMLElement>('[data-node-key]')
    ) {
        const control = ancestor.querySelector<HTMLElement>(`${CONTROL_SELECTOR}--sticky`);
        if (control) covered = Math.max(covered, parseFloat(getComputedStyle(control).top) + control.offsetHeight);
    }
    const box = container.getBoundingClientRect();
    const rect = target.getBoundingClientRect();
    const top = box.top + covered;
    if (rect.top >= top && rect.bottom <= box.bottom) return;
    container.scrollTop += rect.top - top - (box.bottom - top - rect.height) / 2;
}

const addKey = (set: ReadonlySet<string>, key: string): ReadonlySet<string> =>
    set.has(key) ? set : new Set(set).add(key);

const removeKey = (set: ReadonlySet<string>, key: string): ReadonlySet<string> => {
    if (!set.has(key)) return set;
    const next = new Set(set);
    next.delete(key);
    return next;
};

const EMPTY: ReadonlySet<string> = new Set();

interface IControlProps {
    className?: string;
    style?: CSSProperties;
    title?: string;
    ref?: Ref<HTMLElement>;
}

interface IMenuControlProps {
    label: string;
    isOpen: boolean;
    depth: number;
    isSticky: boolean;
    children: ReactElement<IControlProps>;
}

/**
 * Пункт меню: подсказка с подписью у иконки рейла и у обрезанной подписи; раскрытая ветка — липкий заголовок
 * под заголовками предков, «прилипший» — со своей подложкой.
 */
const MenuControl = ({ label, isOpen, depth, isSticky, children }: IMenuControlProps) => {
    const [control, setControl] = useState<HTMLElement | null>(null);
    const sentinelRef = useRef<HTMLDivElement>(null);
    const [isTruncated, setIsTruncated] = useState(false);
    const [isStuck, setIsStuck] = useState(false);
    const [stickyTop, setStickyTop] = useState(depth * STICKY_TOP_PER_LEVEL);

    useEffect(() => {
        if (!isSticky) return;
        const parents: HTMLElement[] = [];
        let group = sentinelRef.current
            ?.closest('.rf-folders-link-group')
            ?.parentElement?.closest('.rf-folders-link-group');
        while (group) {
            const parentControl = group.querySelector<HTMLElement>(CONTROL_SELECTOR);
            if (parentControl) parents.push(parentControl);
            group = group.parentElement?.closest('.rf-folders-link-group');
        }
        const measure = () => setStickyTop(parents.reduce((height, item) => height + item.offsetHeight + 2, 0));
        const observer = new ResizeObserver(measure);
        parents.forEach((item) => observer.observe(item));
        measure();
        return () => observer.disconnect();
    }, [isSticky, depth]);

    useEffect(() => {
        if (!isOpen) {
            setIsTruncated(false);
            return;
        }
        if (!control) return;
        const checkTruncation = () => {
            const labelEl = control.querySelector<HTMLElement>(LABEL_TEXT_SELECTOR);
            if (!labelEl) return;
            setIsTruncated(
                labelEl.scrollHeight - labelEl.clientHeight > 1 || labelEl.scrollWidth - labelEl.clientWidth > 1,
            );
        };
        const observer = new ResizeObserver(checkTruncation);
        observer.observe(control);
        checkTruncation();
        control.addEventListener('mouseenter', checkTruncation);
        return () => {
            observer.disconnect();
            control.removeEventListener('mouseenter', checkTruncation);
        };
    }, [isOpen, control]);

    useEffect(() => {
        if (!isSticky) {
            setIsStuck(false);
            return;
        }
        const sentinel = sentinelRef.current;
        if (!sentinel) return;
        const observer = new IntersectionObserver(([entry]) => setIsStuck(!entry.isIntersecting), {
            root: sentinel.closest<HTMLElement>('.rf-folders-links'),
            threshold: 0,
            rootMargin: `-${stickyTop + 1}px 0px 0px 0px`,
        });
        observer.observe(sentinel);
        return () => observer.disconnect();
    }, [isSticky, stickyTop]);

    const enriched = cloneElement(children, {
        ref: setControl,
        ...(isSticky && {
            className: clsx(children.props.className, isStuck && STUCK_CLASS),
            title: isTruncated ? label : undefined,
            style: { ...children.props.style, '--sticky-top': `${stickyTop}px` } as CSSProperties,
        }),
    });

    // Липкий заголовок без подсказки: обрезанную подпись показывает title.
    const showTooltip = !isSticky && (isOpen ? isTruncated : depth === 0);
    // Подсказку выключают, а не снимают: смена обёртки пересоздала бы пункт — он терял бы фокус.
    return (
        <>
            {isSticky && <div ref={sentinelRef} aria-hidden="true" style={{ height: 1, marginBottom: -1 }} />}
            <Tooltip position="right" background="black" wrapTarget={false} label={label} disabled={!showTooltip}>
                {enriched}
            </Tooltip>
        </>
    );
};

interface IMenuPinButtonProps {
    item: IAppShellMenuItem;
    pinned: boolean;
    /** Кнопка в порядке Tab (полное меню видно) */
    tabbable: boolean;
    onToggle: (id: string) => void;
}

const MenuPinButton = ({ item, pinned, tabbable, onToggle }: IMenuPinButtonProps) =>
    canPinMenuItem(item) ? (
        <button
            type="button"
            className={clsx('rf-folders-pin', pinned && 'rf-folders-pin--saved')}
            aria-label={`${pinned ? 'Открепить' : 'Закрепить'}: ${item.label}`}
            aria-pressed={pinned}
            tabIndex={tabbable ? 0 : -1}
            onClick={() => onToggle(item.id)}
        >
            <MdPushPin />
        </button>
    ) : null;

/** Состояние дерева: пункты берут из него только свои флаги (`MenuNodeSlot`). */
interface IMenuTreeState {
    isOpen: boolean;
    openedNodes: ReadonlySet<string>;
    mountedNodes: ReadonlySet<string>;
    loadingNodes: ReadonlySet<string>;
    transitioningNodes: ReadonlySet<string>;
    pins: string[];
    reduceMotion: boolean;
}

/** Действия пунктов: ссылки постоянные, состояние меню — последнее. */
interface IMenuTreeActions {
    expand: (item: IAppShellMenuItem) => void;
    ensureSidebarOpened: () => void;
    onNavigate: () => void;
    togglePin: (id: string) => void;
    transitionStart: (key: string) => void;
    transitionEnd: (key: string) => void;
    collapseElements: Map<string, HTMLDivElement>;
}

interface IMenuNodeProps {
    item: IAppShellMenuItem;
    depth: number;
    /** Разделитель перед корнем: у соседнего корня того же места другая группа */
    separator: boolean;
    hasTransitioningAncestor: boolean;
    /**
     * Путь к активному пункту — только у пунктов на нём, у остальных `undefined`: переход перерисовывает лишь пункты
     * прежнего и нового пути.
     */
    activePath: string[] | undefined;
    actions: IMenuTreeActions;
}

/** Флаги пункта из состояния дерева: memo пропускает пункты, у которых они не изменились. */
interface IMenuNodeFlags {
    isOpen: boolean;
    opened: boolean;
    mounted: boolean;
    loading: boolean;
    transitioning: boolean;
    pinned: boolean;
    reduceMotion: boolean;
}

const MenuTreeContext = createContext<IMenuTreeState | null>(null);

interface IMenuBadges {
    badges: ReadonlyMap<string, ReactNode> | undefined;
    collapsedBadges: ReadonlyMap<string, ReactNode> | undefined;
}

/** Бейджи — отдельно от состояния дерева: обновление счётчиков перерисовывает только сами бейджи, не пункты. */
const MenuBadgesContext = createContext<IMenuBadges>({ badges: undefined, collapsedBadges: undefined });

const MenuBadge = ({ id, collapsed = false }: { id: string; collapsed?: boolean }) => {
    const { badges, collapsedBadges } = useContext(MenuBadgesContext);
    return (collapsed ? collapsedBadges : badges)?.get(id);
};

/**
 * Пункт дерева с поддеревом. Под memo: переход, обновление счётчиков, раскрытие соседней ветки перерисовывают только
 * пункты, чьи данные, флаги или место на пути к активному пункту изменились.
 */
const MenuNode = memo(function MenuNode({
    item,
    depth,
    separator,
    hasTransitioningAncestor,
    activePath,
    actions,
    isOpen,
    opened: isNodeOpened,
    mounted,
    loading,
    transitioning,
    pinned,
    reduceMotion,
}: IMenuNodeProps & IMenuNodeFlags) {
    const { linkComponent: Link, navigate } = useUi();
    const nodeKey = item.id;
    const nested = isMenuGroup(item);
    const isActive = activePath !== undefined && activePath[activePath.length - 1] === nodeKey;

    const controlContent = (
        <>
            <span className="rf-folders-link-control__left">
                {(depth === 0 || item.icon) && (
                    <span className="rf-folders-link-control__icon">{item.icon ?? <MdOutlineFolder />}</span>
                )}
                <span className="rf-folders-link-control__label">
                    <span className="rf-folders-link-control__label-text">{item.label}</span>
                </span>
            </span>
            <MenuBadge id={nodeKey} />
            {nested && !(isOpen && isNodeOpened) && <MenuBadge id={nodeKey} collapsed />}
            {nested && (
                <MdChevronRight
                    size="16"
                    className={clsx(
                        'rf-folders-link-control__chevron',
                        isNodeOpened && 'rf-folders-link-control__chevron--opened',
                    )}
                />
            )}
        </>
    );

    const isSticky = isOpen && nested && isNodeOpened && !hasTransitioningAncestor;
    const stickyStyle = isSticky
        ? ({
              '--sticky-top': `${depth * STICKY_TOP_PER_LEVEL}px`,
              '--sticky-z': `${10 - depth}`,
          } as CSSProperties)
        : undefined;
    const controlClass = clsx(
        'rf-folders-link-control',
        isActive && 'rf-folders-link-control--active',
        !isActive && activePath !== undefined && 'rf-folders-link-control--ancestor',
        isSticky && 'rf-folders-link-control--sticky',
    );
    const controlAria = {
        'aria-current': isActive ? ('page' as const) : undefined,
        'aria-expanded': nested ? isNodeOpened && isOpen : undefined,
        'aria-label': item.label,
    };

    const collapseRef = useCallback(
        (element: HTMLDivElement | null) => {
            if (element) actions.collapseElements.set(nodeKey, element);
            else actions.collapseElements.delete(nodeKey);
        },
        [actions, nodeKey],
    );
    const onTransitionStart = useCallback(() => actions.transitionStart(nodeKey), [actions, nodeKey]);
    const onTransitionEnd = useCallback(() => {
        // Mantine can retarget height and emit an intermediate end.
        // Sticky descendants need the final overflow:visible state.
        if (isNodeOpened && actions.collapseElements.get(nodeKey)?.style.overflow === 'hidden') return;
        actions.transitionEnd(nodeKey);
    }, [actions, isNodeOpened, nodeKey]);

    const { href } = item;
    const control = href ? (
        <Link
            to={href}
            className={controlClass}
            style={stickyStyle}
            {...controlAria}
            onClick={(event) => {
                if (hasModifier(event)) return;
                actions.ensureSidebarOpened();
                if (!nested) {
                    actions.onNavigate();
                    return;
                }
                event.preventDefault();
                // Ветка с адресом, дети которой ещё не пришли, открывает свой раздел — его страница загрузит
                // ветку, а повторный щелчок её раскроет.
                if (item.children === undefined) {
                    actions.onNavigate();
                    void navigate(href);
                } else actions.expand(item);
            }}
        >
            {controlContent}
        </Link>
    ) : (
        <UnstyledButton
            type="button"
            unstyled
            className={controlClass}
            style={stickyStyle}
            {...controlAria}
            onClick={() => {
                actions.ensureSidebarOpened();
                actions.expand(item);
            }}
        >
            {controlContent}
        </UnstyledButton>
    );

    return (
        <div
            data-node-key={nodeKey}
            className={clsx(
                'rf-folders-link-group',
                depth > 0 && 'rf-folders-link-group--nested',
                separator && 'rf-folders-link-group--separator',
            )}
        >
            <MenuControl label={item.label} isOpen={isOpen} depth={depth} isSticky={isSticky}>
                {control}
            </MenuControl>
            <MenuPinButton item={item} pinned={pinned} tabbable={isOpen} onToggle={actions.togglePin} />

            {nested && (
                <Collapse
                    // Remount on rail/panel switch: the panel mounts the branch in its final
                    // opened state with no animation, and the rail never animates at all.
                    key={isOpen ? 'panel' : 'rail'}
                    className="rf-folders-collapse"
                    ref={collapseRef}
                    expanded={isNodeOpened}
                    keepMounted
                    // Activity pauses nested Collapse effects while the rail is hidden.
                    // Keep them running so preloaded active branches open with the panel.
                    keepMountedMode="display-none"
                    transitionDuration={reduceMotion ? 0 : 190}
                    onTransitionStart={onTransitionStart}
                    onTransitionEnd={onTransitionEnd}
                >
                    {/* The rail hides opened branches instead of collapsing them. */}
                    <div className="rf-folders-link-group__children" hidden={!isOpen}>
                        {!mounted ? null : item.children?.length ? (
                            item.children.map((child) => (
                                <MenuNodeSlot
                                    key={child.id}
                                    item={child}
                                    depth={depth + 1}
                                    separator={false}
                                    hasTransitioningAncestor={hasTransitioningAncestor || transitioning}
                                    activePath={activePath?.includes(child.id) ? activePath : undefined}
                                    actions={actions}
                                />
                            ))
                        ) : loading ? (
                            <div className="rf-folders-link-group__skeleton" aria-busy="true" aria-label="Загрузка">
                                <span className="rf-folders-link-group__skeleton-item" />
                                <span className="rf-folders-link-group__skeleton-item" />
                                <span className="rf-folders-link-group__skeleton-item" />
                            </div>
                        ) : null}
                    </div>
                </Collapse>
            )}
        </div>
    );
});

/** Пункт с флагами из состояния дерева: смена состояния доходит до всех слотов, но перерисовывает только пункты,
 * у которых изменились свои флаги. */
const MenuNodeSlot = (props: IMenuNodeProps) => {
    const tree = useContext(MenuTreeContext);
    if (!tree) return null;
    const id = props.item.id;
    return (
        <MenuNode
            {...props}
            isOpen={tree.isOpen}
            opened={tree.openedNodes.has(id)}
            mounted={tree.mountedNodes.has(id)}
            loading={tree.loadingNodes.has(id)}
            transitioning={tree.transitioningNodes.has(id)}
            pinned={tree.pins.includes(id)}
            reduceMotion={tree.reduceMotion}
        />
    );
};

/**
 * Меню оболочки: поиск по пунктам («/»), закреплённые пункты, дерево разделов с липкими заголовками веток. В рейле —
 * иконки корней с подсказками. Состояние панели (развёрнута, запрос открыть, переход) — из `AppShell`.
 */
const SidebarMenu = ({
    items,
    badges,
    collapsedBadges,
    activeId,
    loadChildren,
    top,
    beforeItems,
    bottom,
    pinsScope,
    pinsLabel = 'Закреплённые разделы',
    searchLoading = false,
    searchFailed = false,
    onSearchRetry,
    opened: isOpenProp,
    onRequestOpen,
    onNavigate: onNavigateProp,
    className,
}: ISidebarMenuProps) => {
    const shell = useAppShell();
    const { storageNamespace, linkComponent: Link } = useUi();
    const isOpen = isOpenProp ?? shell.sidebarOpen;
    const requestOpen = onRequestOpen ?? shell.requestSidebarOpen;
    const onNavigate = onNavigateProp ?? shell.onSidebarNavigate;
    const reduceMotion = useReducedMotion();
    const isEntering = useInitialReveal(items.length > 0);

    const [query, setQuery] = useState('');
    const searchRef = useRef<HTMLInputElement>(null);
    const entries = useMemo(() => flattenMenuItems(items), [items]);
    const searchResults = useMemo(() => searchMenuItems(entries, query), [entries, query]);

    const [storedPins, setPins] = useLocalStorage<string[]>({
        key: sidebarStorageKeys(storageNamespace, shell.storageKey).pins(pinsScope),
        defaultValue: NO_PINS,
        getInitialValueInEffect: false,
    });
    const pins = useMemo(
        () => (Array.isArray(storedPins) ? storedPins.filter((key) => typeof key === 'string') : NO_PINS),
        [storedPins],
    );
    const pinnedEntries = useMemo(
        () =>
            pins
                .map((key) => entries.find((entry) => entry.item.id === key))
                .filter((entry): entry is IAppShellMenuEntry => Boolean(entry && canPinMenuItem(entry.item))),
        [entries, pins],
    );

    const activePath = useMemo(() => findMenuPath(items, activeId), [items, activeId]);
    const activeKey = activePath?.[activePath.length - 1];
    // Страница открыта прямой ссылкой — меню появляется уже раскрытым до пункта, без анимации.
    const [openedNodes, setOpenedNodes] = useState<ReadonlySet<string>>(() => new Set(activePath?.slice(0, -1)));
    const [loadingNodes, setLoadingNodes] = useState<ReadonlySet<string>>(EMPTY);
    const [transitioningNodes, setTransitioningNodes] = useState<ReadonlySet<string>>(EMPTY);
    const [userCollapsedNodes, setUserCollapsedNodes] = useState<ReadonlySet<string>>(EMPTY);
    // Детей ветки монтируем с первого раскрытия и дальше держим. Меню ради поиска догружает дерево целиком, и
    // свёрнутые ветки всего дерева стоили сотни компонентов на каждом переключении рейл/панель (ключ Collapse ниже).
    const [mountedNodes, setMountedNodes] = useState(openedNodes);
    if ([...openedNodes].some((key) => !mountedNodes.has(key))) {
        setMountedNodes(new Set([...mountedNodes, ...openedNodes]));
    }
    const lastScrolledKey = useRef<string | null>(null);
    const linksContainerRef = useRef<HTMLDivElement | null>(null);
    const collapseElements = useRef(new Map<string, HTMLDivElement>());

    useLayoutEffect(() => {
        const viewport = linksContainerRef.current;
        if (!viewport) return;
        const measure = () => {
            viewport.style.setProperty('--menu-scrollbar-width', `${viewport.offsetWidth - viewport.clientWidth}px`);
        };
        const observer = new ResizeObserver(measure);
        observer.observe(viewport);
        measure();
        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        // Remounted Collapses never report the end of an interrupted transition.
        setTransitioningNodes(EMPTY);
        if (!isOpen) {
            setQuery('');
            lastScrolledKey.current = null;
        }
    }, [isOpen]);

    useEffect(() => {
        const handler = (event: KeyboardEvent) => {
            // «/» уже обработан на странице (своя горячая клавиша, поиск в дереве) — не перехватывать
            if (
                event.defaultPrevented ||
                event.key !== '/' ||
                event.ctrlKey ||
                event.metaKey ||
                event.altKey ||
                (event.target as HTMLElement).closest(TYPING_TARGET) ||
                document.querySelector('[role="dialog"]')
            )
                return;
            event.preventDefault();
            requestOpen();
            requestAnimationFrame(() => searchRef.current?.focus());
        };
        document.addEventListener('keydown', handler);
        return () => document.removeEventListener('keydown', handler);
    }, [requestOpen]);

    // Прямая ссылка или переход раскрывают ветки на пути к активному пункту, кроме тех, что пользователь свернул сам
    // на этой странице.
    useEffect(() => {
        if (!activePath || activePath.length < 2) return;
        setOpenedNodes((prev) =>
            activePath
                .slice(0, -1)
                .reduce((next, key) => (userCollapsedNodes.has(key) ? next : addKey(next, key)), prev),
        );
    }, [activePath, userCollapsedNodes]);

    useEffect(() => {
        setUserCollapsedNodes(EMPTY);
        lastScrolledKey.current = null;
    }, [activeId]);

    const revealActiveNode = useCallback(() => {
        const container = linksContainerRef.current;
        if (!activeKey || !container || !activePath?.slice(0, -1).every((key) => openedNodes.has(key))) {
            return false;
        }
        const group = container.querySelector<HTMLElement>(`[data-node-key="${CSS.escape(activeKey)}"]`);
        if (!group) return false;
        lastScrolledKey.current = activeKey;
        revealMenuNode(container, group);
        return true;
    }, [activeKey, activePath, openedNodes]);

    // Раскрытая панель появляется уже прокрученной к активному пункту:
    // ветки в рейле не сворачиваются, поэтому раскладка окончательна до первой отрисовки.
    const wasOpen = useRef(isOpen);
    useLayoutEffect(() => {
        const opened = isOpen && !wasOpen.current;
        wasOpen.current = isOpen;
        if (opened) revealActiveNode();
    }, [isOpen, revealActiveNode]);

    // Один раз на новый активный пункт — после того как Collapse предков доиграл раскрытие, иначе цель ещё нулевой
    // высоты. Повторно не прокручиваем, чтобы не дёргать после ручной прокрутки и щелчков по другим пунктам.
    useEffect(() => {
        if (!isOpen || !activeKey || lastScrolledKey.current === activeKey) return;
        if (!activePath?.slice(0, -1).every((key) => openedNodes.has(key))) return;
        const timeoutId = window.setTimeout(revealActiveNode, 250);
        return () => window.clearTimeout(timeoutId);
    }, [activeKey, activePath, isOpen, openedNodes, revealActiveNode]);

    const ensureSidebarOpened = useCallback(() => {
        if (!isOpen) requestOpen();
    }, [isOpen, requestOpen]);

    const expand = useCallback(
        async (item: IAppShellMenuItem) => {
            const key = item.id;
            if (isMenuBranchUnloaded(item) && loadChildren) {
                if (loadingNodes.has(key)) return;
                setLoadingNodes((prev) => addKey(prev, key));
                setOpenedNodes((prev) => addKey(prev, key));
                setUserCollapsedNodes((prev) => removeKey(prev, key));
                try {
                    await loadChildren(item);
                } catch {
                    // Отказ показывает загрузчик приложения; ветка остаётся свёрнутой.
                    setOpenedNodes((prev) => removeKey(prev, key));
                } finally {
                    setLoadingNodes((prev) => removeKey(prev, key));
                }
                return;
            }
            if (!isMenuGroup(item)) return;
            // В рейле ветка не видна: щелчок по иконке только раскрывает её вместе с панелью.
            const willOpen = !isOpen || !openedNodes.has(key);
            setOpenedNodes((prev) => (willOpen ? addKey(prev, key) : removeKey(prev, key)));
            setUserCollapsedNodes((prev) => (willOpen ? removeKey(prev, key) : addKey(prev, key)));
        },
        [isOpen, loadChildren, loadingNodes, openedNodes],
    );

    const tree = useMemo<IMenuTreeState>(
        () => ({
            isOpen,
            openedNodes,
            mountedNodes,
            loadingNodes,
            transitioningNodes,
            pins,
            reduceMotion,
        }),
        [isOpen, openedNodes, mountedNodes, loadingNodes, transitioningNodes, pins, reduceMotion],
    );
    const menuBadges = useMemo(() => ({ badges, collapsedBadges }), [badges, collapsedBadges]);
    const togglePin = (id: string) => setPins(pins.includes(id) ? pins.filter((key) => key !== id) : [...pins, id]);
    // Пункты получают постоянные действия, иначе memo пункта ломался бы на каждом рендере меню.
    const latest = useLatest({ expand, ensureSidebarOpened, onNavigate, togglePin });
    const actions = useMemo<IMenuTreeActions>(
        () => ({
            expand: (item) => void latest.current.expand(item),
            ensureSidebarOpened: () => latest.current.ensureSidebarOpened(),
            onNavigate: () => latest.current.onNavigate(),
            togglePin: (id) => latest.current.togglePin(id),
            transitionStart: (key) => setTransitioningNodes((prev) => addKey(prev, key)),
            transitionEnd: (key) => setTransitioningNodes((prev) => removeKey(prev, key)),
            collapseElements: collapseElements.current,
        }),
        [],
    );

    const renderRoots = (placement: 'main' | 'footer') =>
        items.map((item, index) => {
            if ((item.placement ?? 'main') !== placement) return <Fragment key={item.id} />;
            // Разделитель — со сменой группы у соседнего корня того же места (список или низ панели).
            const previous = items.slice(0, index).findLast((root) => (root.placement ?? 'main') === placement);
            return (
                <MenuNodeSlot
                    key={item.id}
                    item={item}
                    depth={0}
                    separator={previous !== undefined && item.group !== previous.group}
                    hasTransitioningAncestor={false}
                    activePath={activePath?.includes(item.id) ? activePath : undefined}
                    actions={actions}
                />
            );
        });

    const renderShortcut = ({ item, parents, ancestorIds }: IAppShellMenuEntry, result = false) => {
        const nested = isMenuGroup(item);
        const path = result && parents.length > 0 ? parents.join(' / ') : '';
        // В рейле подпись скрыта display:none и не входит в имя — имя задаём явно.
        const name = path ? `${item.label} (${path})` : item.label;
        const content = (
            <>
                <span className="rf-folders-link-control__icon">
                    <MdOutlineDescription />
                </span>
                <span className="rf-folders-link-control__label">
                    <span className="rf-folders-link-control__label-text">{item.label}</span>
                    {path && <small>{path}</small>}
                </span>
            </>
        );
        const activate = () => {
            setQuery('');
            if (!nested) {
                onNavigate();
                return;
            }
            ensureSidebarOpened();
            const branch = [...ancestorIds, item.id];
            setOpenedNodes((prev) => new Set([...prev, ...branch]));
            setUserCollapsedNodes((prev) => new Set([...prev].filter((value) => !branch.includes(value))));
            requestAnimationFrame(() => {
                const container = linksContainerRef.current;
                const group = container?.querySelector<HTMLElement>(`[data-node-key="${CSS.escape(item.id)}"]`);
                if (container && group) revealMenuNode(container, group);
            });
        };
        return (
            <div key={item.id} className="rf-folders-shortcut">
                {nested || !item.href ? (
                    <button
                        type="button"
                        className="rf-folders-link-control"
                        aria-label={name}
                        title={item.label}
                        onClick={activate}
                    >
                        {content}
                    </button>
                ) : (
                    <Link
                        to={item.href}
                        className={clsx(
                            'rf-folders-link-control',
                            activeKey === item.id && 'rf-folders-link-control--active',
                        )}
                        aria-label={name}
                        title={item.label}
                        onClick={(event) => {
                            if (!event.ctrlKey && !event.metaKey && !event.shiftKey && !event.altKey) activate();
                        }}
                    >
                        {content}
                    </Link>
                )}
                <MenuPinButton
                    item={item}
                    pinned={pins.includes(item.id)}
                    tabbable={isOpen}
                    onToggle={actions.togglePin}
                />
            </div>
        );
    };

    return (
        <MenuTreeContext value={tree}>
            <MenuBadgesContext value={menuBadges}>
                <div
                    className={clsx('rf-folders', isOpen ? 'rf-folders--open' : 'rf-folders--close', className)}
                    data-entering={isEntering}
                >
                    {top}
                    <div className="rf-folders-search">
                        <button
                            type="button"
                            className="rf-folders-search__icon"
                            aria-label="Поиск по меню"
                            onClick={() => {
                                ensureSidebarOpened();
                                requestAnimationFrame(() => searchRef.current?.focus());
                            }}
                        >
                            <MdSearch />
                        </button>
                        <input
                            ref={searchRef}
                            value={query}
                            onChange={(event) => setQuery(event.target.value)}
                            aria-label="Найти в меню"
                            placeholder="Найти в меню"
                            aria-keyshortcuts="/"
                            tabIndex={isOpen ? 0 : -1}
                            onKeyDown={(event) => {
                                if (event.key === 'Escape' && query) {
                                    event.stopPropagation();
                                    setQuery('');
                                }
                            }}
                        />
                        {query ? (
                            <button
                                type="button"
                                aria-label="Очистить поиск"
                                onClick={() => {
                                    setQuery('');
                                    searchRef.current?.focus();
                                }}
                            >
                                <MdClose />
                            </button>
                        ) : (
                            // Клавиша объявлена полю через aria-keyshortcuts — чип остаётся
                            // декорацией и не попадает в чтение скринридером дважды.
                            <span className="rf-folders-search__kbd" aria-hidden="true">
                                /
                            </span>
                        )}
                    </div>
                    {beforeItems}

                    <div ref={linksContainerRef} className="rf-folders-links">
                        <div className="rf-folders-links__inner">
                            {query.trim() ? (
                                <>
                                    <div className="rf-folders-search-status" role="status">
                                        {searchLoading
                                            ? 'Поиск по разделам…'
                                            : searchResults.length
                                              ? `Найдено: ${searchResults.length}`
                                              : 'Ничего не найдено'}
                                    </div>
                                    {searchFailed && onSearchRetry && (
                                        <button type="button" className="rf-folders-retry" onClick={onSearchRetry}>
                                            Не все разделы загружены. Повторить
                                        </button>
                                    )}
                                    {searchResults.map((entry) => renderShortcut(entry, true))}
                                </>
                            ) : (
                                <>
                                    {pinnedEntries.length > 0 && (
                                        <section className="rf-folders-pins" aria-label={pinsLabel}>
                                            {pinnedEntries.map((entry) => renderShortcut(entry))}
                                        </section>
                                    )}
                                    {renderRoots('main')}
                                </>
                            )}
                        </div>
                    </div>

                    {items.some((item) => item.placement === 'footer') && (
                        <div className="rf-folders-footer">{renderRoots('footer')}</div>
                    )}
                    {bottom}
                </div>
            </MenuBadgesContext>
        </MenuTreeContext>
    );
};

export default memo(SidebarMenu);
